import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubSupportTicketMessageThread } from "@/components/HubSupportTicketMessageThread";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { CardTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

import { fetchQualityCaseMessages, postQualityCaseMessage } from "./casesApi";

export function QualityCaseNotesCard({ caseId }: { caseId: number }): JSX.Element {
  const writesAllowed = useHubModuleWrite("quality");
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const messagesQuery = useQuery({
    queryKey: ["quality", "case", caseId, "messages"],
    queryFn: () => fetchQualityCaseMessages(caseId),
    enabled: Number.isFinite(caseId) && caseId > 0,
  });

  const postMutation = useMutation({
    mutationFn: (body: string) => postQualityCaseMessage(caseId, body),
    onSuccess: async () => {
      setDraft("");
      setError(null);
      showToast({ tone: "success", message: "Note added." });
      await queryClient.invalidateQueries({ queryKey: ["quality", "case", caseId, "messages"] });
      await queryClient.invalidateQueries({ queryKey: ["quality", "case", caseId, "activity"] });
    },
    onError: (postError: Error) => setError(postError.message),
  });

  const messages = (messagesQuery.data?.messages ?? []).map((message) => ({
    id: message.id,
    created_at: message.createdAt,
    author_user_id: message.authorUserId,
    author_email: message.authorEmail,
    author_display_name: message.authorDisplayName,
    body: message.body,
    is_from_reporter: false,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHelp hoverHint="Append-only notes on this case. Each note keeps its author and time. Notes are not edited in place.">
          Case notes
        </CardTitleWithHelp>
      </CardHeader>
      <CardBody>
        {messagesQuery.isError ? (
          <p className="mb-3 text-sm text-red-700">{(messagesQuery.error as Error).message}</p>
        ) : null}
        <HubSupportTicketMessageThread
          messages={messages}
          draft={draft}
          onDraftChange={setDraft}
          onSubmit={() => {
            if (draft.trim() === "") return;
            postMutation.mutate(draft.trim());
          }}
          submitting={postMutation.isPending}
          canReply={writesAllowed}
          error={error}
          composeLabel="Add note"
          viewer="admin"
          staffPartyLabel="Quality"
          sectionLabel="Notes"
          emptyMessage="No notes yet. Add one to start the thread."
          placeholder="What should the next person on this case know?"
        />
      </CardBody>
    </Card>
  );
}
